<template>
  <v-dialog
    :value="show"
    persistent
    max-width="1100px"
    scrollable
    transition="dialog-transition"
  >
    <v-card>
      <v-card-title class="grey lighten-3">
        {{ title }}
      </v-card-title>
      <v-card-text class="py-3">
        <v-card class="pa-3">
          <v-row>
            <v-col cols="12" sm="3">
              <v-text-field
                label="Start Date"
                :value="$options.filters.formatDate(startDate)"
                readonly
                disabled
                hide-details
                :outlined="outlinedInputs"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-text-field
                label="Return Date"
                :value="$options.filters.formatDate(returnDate)"
                readonly
                disabled
                hide-details
                :outlined="outlinedInputs"
              ></v-text-field>
            </v-col>
            <v-col cols="4" sm="2">
              <v-text-field
                type="number"
                label="Adults"
                v-model.number="headerAdults"
                @input="onHeaderPaxChange"
                min="0"
                hide-details
                :outlined="outlinedInputs"
                :disabled="$apollo.loading"
              ></v-text-field>
            </v-col>
            <v-col cols="4" sm="2">
              <v-text-field
                type="number"
                label="Children"
                v-model.number="headerChildren"
                @input="onHeaderPaxChange"
                min="0"
                hide-details
                :outlined="outlinedInputs"
                :disabled="$apollo.loading"
              ></v-text-field>
            </v-col>
            <v-col cols="4" sm="2">
              <v-text-field
                type="number"
                label="Infants"
                v-model.number="headerInfants"
                @input="onHeaderPaxChange"
                min="0"
                hide-details
                :outlined="outlinedInputs"
                :disabled="$apollo.loading"
              ></v-text-field>
            </v-col>
          </v-row>

          <div class="caption grey--text text--darken-1 pt-2">
            Remaining on this departure: {{ remainingAdults }} adults,
            {{ remainingChildren }} children.
          </div>
          <v-alert
            v-if="availabilityWarning"
            type="warning"
            dense
            outlined
            class="mt-2 mb-0"
          >
            {{ availabilityWarning }}
          </v-alert>
        </v-card>

        <v-card class="mt-3">
          <v-card-title class="subtitle-1 grey lighten-4 py-2">
            Services
            <v-spacer></v-spacer>
            <span class="caption grey--text text--darken-1">
              {{ selectedRows.length }} of {{ rows.length }} selected
            </span>
          </v-card-title>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th style="width: 48px">
                    <v-simple-checkbox
                      :value="allSelected"
                      :indeterminate="someSelected"
                      :disabled="$apollo.loading || !rows.length"
                      @input="onToggleAll"
                    ></v-simple-checkbox>
                  </th>
                  <th>Supplier</th>
                  <th>Service</th>
                  <th style="width: 60px">Day</th>
                  <th style="width: 90px">Qty</th>
                  <th style="width: 90px">Adults</th>
                  <th style="width: 90px">Children</th>
                  <th style="width: 90px">Infants</th>
                  <th style="width: 110px">Availability</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!rows.length">
                  <td colspan="9" class="text-center grey--text py-3">
                    {{
                      $apollo.loading
                        ? "Loading services…"
                        : "This departure has no services."
                    }}
                  </td>
                </tr>
                <tr
                  v-for="row in rows"
                  :key="row.id"
                  :class="{ 'grey--text': !row.selected }"
                >
                  <td>
                    <v-simple-checkbox
                      :value="row.selected"
                      :disabled="$apollo.loading"
                      @input="onToggleRow(row, $event)"
                    ></v-simple-checkbox>
                  </td>
                  <td>{{ row.supplierName }}</td>
                  <td>
                    {{ row.serviceName }}
                    <span v-if="row.typeName" class="caption grey--text">
                      ({{ row.typeName }})
                    </span>
                  </td>
                  <td>{{ row.day }}</td>
                  <td>
                    <v-text-field
                      type="number"
                      v-model.number="row.qty"
                      min="0"
                      dense
                      hide-details
                      single-line
                      :disabled="!row.selected || $apollo.loading"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      type="number"
                      v-model.number="row.adults"
                      @input="onRowPaxChange(row)"
                      min="0"
                      dense
                      hide-details
                      single-line
                      :disabled="!row.selected || $apollo.loading"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      type="number"
                      v-model.number="row.children"
                      @input="onRowPaxChange(row)"
                      min="0"
                      dense
                      hide-details
                      single-line
                      :disabled="!row.selected || $apollo.loading"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      type="number"
                      v-model.number="row.infants"
                      min="0"
                      dense
                      hide-details
                      single-line
                      :disabled="!row.selected || $apollo.loading"
                    ></v-text-field>
                  </td>
                  <td
                    class="caption"
                    :class="{ 'error--text': isRowOverbooked(row) }"
                  >
                    {{ row.remaining }} of {{ row.total }} left
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>

        <v-card class="mt-3">
          <v-container>
            <participants-list
              :participants="participants"
              @add="onAddParticipant"
              @replace="onReplaceParticipant"
              @remove="onRemoveParticipant"
              hideEditClient
              :disabled="$apollo.loading"
              :loading="$apollo.loading"
            ></participants-list>
          </v-container>
        </v-card>

        <v-container fluid px-0 pb-0>
          <v-alert type="info" outlined dense>
            The itinerary start date is fixed to the departure date. Only the
            selected services are added; services without a custom price will be
            priced from the supplier rates.
          </v-alert>
        </v-container>
      </v-card-text>

      <v-card-actions class="grey lighten-4">
        <v-spacer></v-spacer>
        <v-btn color="warning" text @click="$emit('close')" :disabled="saving">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="onCreateItinerary"
          :loading="saving"
          :disabled="!canCreate"
        >
          Create Itinerary
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import gql from "graphql-tag";
import ParticipantsList from "../../itineraries/builder/ItineraryBuilderParticipantsList2";
import { OPEN_GROUP_DEPARTURE_QUERY } from "../departure-gql";
import { sortBy, sortByFn, removeGQLTypename } from "../../../../helpers";
var randomize = require("randomatic");
var moment = require("moment");

const CREATE_ITINERARY = gql`
  mutation createItinerary($input: ItineraryInput!) {
    createItinerary(itineraryInput: $input) {
      id
      reference
    }
  }
`;

export default {
  name: "OpenGroupDepartureBookDialog",

  props: {
    show: { type: Boolean, default: false },
    departure: { type: Object, default: () => null },
  },

  components: {
    ParticipantsList,
  },

  data() {
    return {
      openGroupDeparture: null,
      //#11411 — one row per departure service, picked individually
      rows: [],
      headerAdults: 0,
      headerChildren: 0,
      headerInfants: 0,
      participants: [],
      saving: false,
    };
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    title() {
      return this.departure
        ? `Book Departure — ${this.departure.name}`
        : "Book Departure";
    },
    details() {
      return this.openGroupDeparture || this.departure || {};
    },
    startDate() {
      return this.details.startDate;
    },
    returnDate() {
      return this.details.returnDate;
    },
    remainingAdults() {
      return Number(this.details.remainingAdults) || 0;
    },
    remainingChildren() {
      return Number(this.details.remainingChildren) || 0;
    },
    departureItems() {
      return this.openGroupDeparture && this.openGroupDeparture.items
        ? this.openGroupDeparture.items
        : [];
    },
    selectedRows() {
      return this.rows.filter((row) => row.selected);
    },
    allSelected() {
      return this.rows.length > 0 && this.selectedRows.length == this.rows.length;
    },
    someSelected() {
      return this.selectedRows.length > 0 && !this.allSelected;
    },
    //header pax drives the itinerary header and seeds every selected row
    totals() {
      return {
        adults: Number(this.headerAdults) || 0,
        children: Number(this.headerChildren) || 0,
        infants: Number(this.headerInfants) || 0,
      };
    },
    //#11180 — overbooking is allowed: these are warnings, they do not block Create
    //#11411 — report how far over the departure ends up, not the (possibly negative) remaining
    availabilityWarning() {
      const over = [];
      if (this.totals.adults > this.remainingAdults) {
        over.push(`${this.totals.adults - this.remainingAdults} adult places`);
      }
      if (this.totals.children > this.remainingChildren) {
        over.push(
          `${this.totals.children - this.remainingChildren} child places`
        );
      }
      let roomsOver = false;
      for (const row of this.selectedRows) {
        if (this.isRowOverbooked(row)) {
          roomsOver = true;
          over.push(
            `${Number(row.qty) - Number(row.remaining)} x ${row.serviceName}`
          );
        }
      }
      //#11180 — rooms over their limit are booked with service status Provisional
      const provisional = roomsOver
        ? " Overbooked rooms will be booked as Provisional."
        : "";
      return over.length
        ? `This booking overbooks the departure. Overbooked by ${over.join(
            ", "
          )}.${provisional}`
        : "";
    },
    somethingSelected() {
      return (
        this.selectedRows.length > 0 &&
        (this.totals.adults > 0 ||
          this.totals.children > 0 ||
          this.totals.infants > 0)
      );
    },
    canCreate() {
      return (
        !this.saving &&
        !!this.openGroupDeparture &&
        this.participants.length > 0 &&
        this.somethingSelected
      );
    },
  },

  watch: {
    show(open) {
      if (!open) return;
      this.participants = [];
      this.headerAdults = 0;
      this.headerChildren = 0;
      this.headerInfants = 0;
      this.rows = this.openGroupDeparture ? this.getRows() : [];
    },
  },

  methods: {
    //#11411 — flat list of every service on the departure, sorted day then order
    getRows() {
      return this.departureItems
        .map((item) => {
          const isAccommodation =
            !!item.supplier && item.supplier.serviceType == "ACCOMMODATION";
          return {
            id: randomize("Aa0", 32),
            item,
            selected: true,
            supplierName: item.supplier ? item.supplier.name : "",
            serviceName: item.service ? item.service.name : "",
            typeName:
              item.service && item.service.typeName != item.service.name
                ? item.service.typeName
                : "",
            serviceType: item.supplier ? item.supplier.serviceType : "",
            isAccommodation,
            day: Number(item.day || 1) || 1,
            order: Number(item.order) || 0,
            occupancy: Number(item.service ? item.service.stdOccupancy : 1) || 1,
            qty: 0,
            adults: 0,
            children: 0,
            infants: 0,
            remaining: Number(item.remainingQty) || 0,
            total: Number(item.qty) || 0,
          };
        })
        .sort(sortByFn((row) => row.day * 100000 + row.order));
    },
    isRowOverbooked(row) {
      return (
        row.selected && row.total > 0 && Number(row.qty) > Number(row.remaining)
      );
    },
    //qty follows pax / occupancy, but stays hand-editable afterwards
    recalcRowQty(row) {
      const pax = Number(row.adults) + Number(row.children);
      row.qty = Math.ceil(pax / (row.occupancy || 1)) || 0;
    },
    applyHeaderPax(row) {
      row.adults = this.totals.adults;
      row.children = this.totals.children;
      row.infants = this.totals.infants;
      this.recalcRowQty(row);
    },
    onHeaderPaxChange() {
      for (const row of this.selectedRows) {
        this.applyHeaderPax(row);
      }
    },
    onRowPaxChange(row) {
      this.recalcRowQty(row);
    },
    onToggleRow(row, selected) {
      row.selected = selected;
      if (selected) {
        this.applyHeaderPax(row);
      } else {
        row.qty = 0;
        row.adults = 0;
        row.children = 0;
        row.infants = 0;
      }
    },
    onToggleAll(selected) {
      for (const row of this.rows) {
        this.onToggleRow(row, selected);
      }
    },
    onAddParticipant(payload) {
      const participant = payload
        ? { ...payload }
        : { title: "Mr.", name: "", surname: "", email: "", mobile: "" };
      this.participants.push(participant);
    },
    onReplaceParticipant(payload) {
      if (payload.index > -1) {
        Object.assign(this.participants[payload.index], { ...payload.new });
      }
    },
    onRemoveParticipant(index) {
      this.participants.splice(index, 1);
    },
    toItineraryParticipantInput(item) {
      return {
        id: item.id ? item.id : undefined,
        title: item.title,
        name: item.name,
        surname: item.surname,
        email: item.email,
        mobile: item.mobile,
      };
    },
    getItineraryItems() {
      const dep = this.openGroupDeparture;
      return this.selectedRows
        .map((row) => {
          const item = row.item;
          const itemStartDate = moment(dep.startDate).add(row.day - 1, "days");
          let itemDuration = Number(item.duration || 1) || 1;
          //non-accommodation services span duration-1 nights (see #6893)
          if (!row.isAccommodation) {
            itemDuration = Math.max(itemDuration - 1, 0);
          }
          const itemEndDate = moment(itemStartDate).add(itemDuration, "days");
          return {
            id: randomize("Aa0", 32),
            uid: item.uid || randomize("Aa0", 32),
            parentUid: item.parentUid,
            //11180 - provenance: which departure item this service was copied from
            openGroupDepartureItemId: item.id,
            order: row.order,
            serviceId: item.service ? item.service.id : null,
            adults: Number(row.adults) || 0,
            children: Number(row.children) || 0,
            infants: Number(row.infants) || 0,
            qty: Number(row.qty) || 0,
            startDate: itemStartDate.toISOString(true),
            endDate: itemEndDate.toISOString(true),
            isIncluded: item.included,
            isOptional: item.optional,
            isHidden: item.hidden,
            includeCommission: true,
            customPricing: !!item.customPrice,
            pricing: item.customPrice
              ? {
                  adultBuy: item.adultBuy || 0,
                  adultSell: item.adultSell || 0,
                  childBuy: item.childBuy || 0,
                  childSell: item.childSell || 0,
                  infantBuy: item.infantBuy || 0,
                  infantSell: item.infantSell || 0,
                  itemBuy: item.itemBuy || 0,
                  itemSell: item.itemSell || 0,
                  rateTypeCode: item.rateTypeCode,
                  currencyBuy: item.currencyBuy,
                  currencySell: item.currencySell,
                }
              : undefined,
            remarks: item.remarks
              ? removeGQLTypename({ ...item.remarks })
              : undefined,
          };
        })
        .filter((item) => item.serviceId && item.qty);
    },
    toItiItemInput(item, allItems) {
      let parentId = undefined;
      if (item.parentUid && allItems) {
        const parentItem = allItems.find((i) => i.uid === item.parentUid);
        if (parentItem) parentId = parentItem.id;
      }
      const input = { ...item, parentId };
      delete input.uid;
      delete input.parentUid;
      delete input.order;
      return input;
    },
    getItineraryInput() {
      const dep = this.openGroupDeparture;
      const master = dep.openGroup || {};
      const allItems = this.getItineraryItems().sort(sortBy("order"));
      return {
        adults: String(this.totals.adults),
        children: String(this.totals.children),
        infants: String(this.totals.infants),
        department: dep.department || master.department || "",
        brand: dep.brand || master.brand || undefined,
        destinationId: master.destination ? master.destination.id : "",
        agentId: this.$store.getters.agentId,
        startDate: moment(dep.startDate).format("YYYY-MM-DD"),
        endDate: moment(dep.returnDate).format("YYYY-MM-DD"),
        participants: this.participants.map(this.toItineraryParticipantInput),
        items: allItems.map((item) => this.toItiItemInput(item, allItems)),
        openGroupDepartureId: dep.id,
      };
    },
    onCreateItinerary() {
      this.saving = true;
      this.$apollo
        .mutate({
          mutation: CREATE_ITINERARY,
          variables: { input: this.getItineraryInput() },
        })
        .then(({ data }) => {
          this.saving = false;
          this.$emit("created", data.createItinerary);
        })
        .catch((err) => {
          this.saving = false;
          const msg = err.graphQLErrors
            ? err.graphQLErrors.map((e) => e.message).join(", ")
            : err.message;
          this.$store.dispatch("showAppSnackbar", { msg, color: "error" });
        });
    },
  },

  apollo: {
    openGroupDeparture: {
      query: OPEN_GROUP_DEPARTURE_QUERY,
      variables() {
        return { id: this.departure.id };
      },
      skip() {
        return !this.show || !this.departure;
      },
      fetchPolicy: "network-only",
      result({ data }) {
        if (data && data.openGroupDeparture) {
          this.rows = this.getRows();
        }
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/open-groups/builder/OpenGroupDepartureBookDialog.vue